import { intl } from '../../../../i18n'
import { generateUuid } from '../../../../utils/uuids'
import { generateCode } from '../../../../utils/code'
import { UserBasic } from '../../../../lib/functions/user'
import { DivisionProps } from '../../../../lib/functions/division'
import {
  createDelta,
  IItemDelta,
} from '../../../../domain/value-object/ItemDeltaInputVO'
import {
  ProgramStatus,
  ProgramCreateInput,
  ProgramUpdateDeltaInput,
} from '../../../../infrastructure/api/programs'
import { ListRow } from '../../../containers/BulkSheetView/model'

export interface ProgramRow extends ListRow {
  code: string
  displayName: string
  status: ProgramStatus | null
  scheduledStartDate: string | null
  scheduledEndDate: string | null
  division: DivisionProps | null
  programManager?: UserBasic
  collaborators: UserBasic[]
}

const toIsoDate = (value: string | null | undefined): string | null =>
  value ? value.replace(/\//g, '-') : null

const getCollaboratorUuids = (collaborators: UserBasic[]): string[] =>
  collaborators.map(collaborator => collaborator.uuid)

export const createNewProgramRow = (existingCodes: string[]): ProgramRow => ({
  uuid: generateUuid(),
  code: generateCode(existingCodes),
  displayName: '',
  status: 'STANDBY',
  scheduledStartDate: null,
  scheduledEndDate: null,
  division: null,
  collaborators: [],
  revision: '',
  lockVersion: 0,
  added: true,
})

export const buildCreateInput = (row: ProgramRow): ProgramCreateInput => ({
  uuid: row.uuid,
  code: row.code?.trim() ? row.code : undefined,
  displayName: row.displayName,
  status: row.status!,
  programManagerUuid: row.programManager!.uuid,
  divisionUuid: row.division?.uuid,
  scheduledStartDate: toIsoDate(row.scheduledStartDate),
  scheduledEndDate: toIsoDate(row.scheduledEndDate),
  collaboratorUuids: getCollaboratorUuids(row.collaborators),
})

const collaboratorDelta = (
  oldUuids: string[],
  newUuids: string[]
): IItemDelta<string[]> | undefined => {
  const isSame =
    oldUuids.length === newUuids.length &&
    oldUuids.every((uuid, index) => uuid === newUuids[index])
  return isSame ? undefined : { oldValue: oldUuids, newValue: newUuids }
}

export const buildEditedDelta = (
  row: ProgramRow,
  original: ProgramRow
): ProgramUpdateDeltaInput => ({
  uuid: row.uuid,
  displayName: createDelta(original.displayName, row.displayName),
  status: createDelta(original.status ?? undefined, row.status ?? undefined),
  programManagerUuid: createDelta(
    original.programManager?.uuid,
    row.programManager?.uuid
  ),
  divisionUuid: createDelta(original.division?.uuid, row.division?.uuid),
  scheduledStartDate: createDelta(
    toIsoDate(original.scheduledStartDate),
    toIsoDate(row.scheduledStartDate)
  ),
  scheduledEndDate: createDelta(
    toIsoDate(original.scheduledEndDate),
    toIsoDate(row.scheduledEndDate)
  ),
  collaboratorUuids: collaboratorDelta(
    getCollaboratorUuids(original.collaborators),
    getCollaboratorUuids(row.collaborators)
  ),
})

export const validateScheduledDateOrder = (
  row: Pick<ProgramRow, 'scheduledStartDate' | 'scheduledEndDate'>
): string | undefined => {
  const start = toIsoDate(row.scheduledStartDate)
  const end = toIsoDate(row.scheduledEndDate)
  return start && end && start > end
    ? intl.formatMessage({ id: 'programList.validation.scheduledDateInverted' })
    : undefined
}
